import type { ComponentPropsWithRef, FC } from 'react';
import { useEffect, useId } from 'react';
import mergeRefs from 'react-merge-refs';
import { ErrorMessage } from 'src/components/form/error-message';
import { useFieldGroup } from 'src/components/form/field-group';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import type { FormFieldProps } from 'src/components/form/form-field-props';
import { FormHint } from 'src/components/form/form-hint';
import { FieldLabel } from 'src/components/form/label';
import {
  formFieldBaseClasses,
  formFieldDisabledClasses,
  formFieldErrorClasses,
} from 'src/components/form/styles';
import { useCustomField } from 'src/components/form/use-custom-field';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Tooltip } from 'src/components/overlay/tooltip';
import { useDefaultStacked } from 'src/hooks/use-default-stacked';
import { cn } from 'src/utils/cn';

export type SimpleTextareaProps = {
  error?: boolean;
} & ComponentPropsWithRef<'textarea'>;

export const SimpleTextarea: FC<SimpleTextareaProps> = ({
  error,
  ...props
}) => {
  return (
    <textarea
      {...props}
      className={cn(
        formFieldBaseClasses,
        `block min-h-[8rem] resize-y`,
        error && formFieldErrorClasses,
        formFieldDisabledClasses,
        props.className
      )}
    />
  );
};

export type TextareaProps = {
  stacked?: boolean;
  nested?: boolean;
} & FormFieldProps &
  ComponentPropsWithRef<'textarea'>;

/**
 * Use this component whenever a user needs to provide a longer text input.
 *
 * For shorter text input you can use [Input](../?path=/docs/components-form-input--docs).
 *
 * If the user needs to provide Markdown use [Markdown Editor](../?path=/docs/components-form-markdown-editor--docs) instead.
 */
export const Textarea: FC<TextareaProps> = ({ ref, ...props }) => {
  const { isStacked, minTablet } = useDefaultStacked();

  const {
    label,
    name,
    stacked = isStacked,
    nested,
    hideLabel = false,
    hint,
    info,
    markAsRequired,
    ...inputProps
  } = props;
  const id = useId();

  const [field, showError, error] = useCustomField<string | null>(name, label);

  const formGroup = useFieldGroup();
  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  const focusRef = useFocusManager();

  const fieldId = formGroup?.id || id;
  const displayLabel = `${label}${markAsRequired ? '*' : ''}`;
  const ariaLabel = hideLabel ? displayLabel : undefined;

  const fieldItem = (
    <FieldItem>
      <Tooltip
        content={
          inputProps.disabled && inputProps.disabledMessage
            ? inputProps.disabledMessage
            : undefined
        }
      >
        {(targetProps) => (
          <SimpleTextarea
            ref={mergeRefs([focusRef, ref!])}
            id={fieldId}
            error={showError}
            data-test-error={showError}
            data-testid={field.name}
            {...field}
            aria-label={ariaLabel}
            {...inputProps}
            // if value is null, default to empty string
            value={field.value ?? ''}
            {...targetProps}
          />
        )}
      </Tooltip>

      {hint && (
        <FormHint isDisabled={props.disabled} data-testid={`${field.name}Hint`}>
          {hint}
        </FormHint>
      )}

      {showError && error && (
        <ErrorMessage
          data-testid={`${name}Error`}
          error={error}
          label={label}
        />
      )}
    </FieldItem>
  );

  return ((formGroup && !minTablet) || !formGroup) && !hideLabel ? (
    <FieldLayout
      stacked={stacked}
      className={cn(
        // When having a textarea instead another input or element with text, it causes the baseline alignment to break.
        // So we cover this edge case manually by aligning the label to the top and shifting it a bit down.
        // Not ideal 😿, but oh well.
        // TODO: Investigate further why this happens.
        // Do not apply when stacked: translate does not affect layout, so it collapses the visual gap above the control.
        inputProps.disabled &&
          !stacked &&
          `[&_div:first-child]:translate-y-md [&_div:first-child]:self-start`
      )}
    >
      <FieldLabel
        name={name}
        displayLabel={displayLabel}
        fieldId={fieldId}
        info={info}
        showError={showError}
        nested={nested}
      />

      {fieldItem}
    </FieldLayout>
  ) : (
    fieldItem
  );
};
